import simplify from 'simplify-js';
import * as turf from '@turf/turf';
import {IPoint} from '../incase-domain/elements/question/imageMarkup';

export const getPixelData = (canvas: HTMLCanvasElement | null): IPoint[] => {
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return [];
    const {data} = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const rasterPoints = [];

    for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
            const index = (y * canvas.width + x) * 4;
            const alpha = data[index + 3];

            if (alpha > 0) {
                rasterPoints.push({x, y});
            }
        }
    }

    return rasterPoints;
};

export const getRasterizedShapesIntersection = (shape1: IPoint[] | undefined, shape2: IPoint[] | undefined): number => {
    if (!shape1 || !shape2) return 0;
    const set2 = new Set(shape2.map((p) => `${p.x},${p.y}`));

    const intersection = shape1.filter((p) => set2.has(`${p.x},${p.y}`));
    return intersection.length / (shape1.length + shape2.length - intersection.length);
};

export const isPointInsideArea = (point?: IPoint, shape?: IPoint[]): boolean => {
    if (!point || !shape || shape.length < 3) {
        return false;
    }
    const {x, y} = point;
    return shape.some((p) => p.x === x && p.y === y);
};

/**
 * Simplify polyline points using simplify-js (Douglas-Peucker + Radial Distance)
 * Reduces the number of points while maintaining the overall shape
 * @param points Array of points to simplify
 * @param tolerance Simplification tolerance (higher = more simplification, typically 0.001-0.01 for normalized 0-1 coordinates)
 * @param highQuality Use high quality mode (slower but more accurate)
 * @returns Simplified array of points
 */
export const simplifyPoints = (points: IPoint[], tolerance = 0.01, highQuality = false): IPoint[] => {
    if (!points || points.length <= 2) return points;

    return simplify(points, tolerance, highQuality);
};

/**
 * Calculate polygon area using shoelace formula (for normalized 0-1 coordinates)
 * @param points Polygon points (normalized 0-1)
 * @returns Area in normalized units (0-1 range)
 */
const getPolygonArea = (points: IPoint[]): number => {
    if (!points || points.length < 3) return 0;

    let area = 0;
    const n = points.length;

    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i].x * points[j].y;
        area -= points[j].x * points[i].y;
    }

    return Math.abs(area) / 2;
};

/**
 * Clean and validate polygon points
 * Removes duplicate consecutive points and validates coordinates
 */
const cleanPolygonPoints = (points: IPoint[]): IPoint[] => {
    if (!points || points.length === 0) return [];

    const cleaned: IPoint[] = [];
    const tolerance = 0.0001; // Very small tolerance for normalized coordinates

    for (let i = 0; i < points.length; i++) {
        const point = points[i];

        if (cleaned.length === 0) {
            cleaned.push(point);
        } else {
            const lastPoint = cleaned[cleaned.length - 1];
            const dx = Math.abs(point.x - lastPoint.x);
            const dy = Math.abs(point.y - lastPoint.y);
            if (dx > tolerance || dy > tolerance) {
                cleaned.push(point);
            }
        }
    }

    return cleaned;
};

/**
 * Convert normalized points (0-1 range) to GeoJSON polygon coordinates
 * GeoJSON uses [longitude, latitude] format, but we'll use [x, y] for our 2D canvas
 */
const pointsToGeoJSONPolygon = (points: IPoint[]): ReturnType<typeof turf.polygon> => {
    const cleanedPoints = cleanPolygonPoints(points);

    const closedPoints = [...cleanedPoints];
    const first = closedPoints[0];
    const last = closedPoints[closedPoints.length - 1];
    const tolerance = 0.0001;

    if (Math.abs(first.x - last.x) > tolerance || Math.abs(first.y - last.y) > tolerance) {
        closedPoints.push({...first});
    }

    const coordinates = closedPoints.map((p) => [p.x, p.y]);

    return turf.polygon([coordinates]);
};

/**
 * Calculate polygon intersection percentage using Turf.js for intersection,
 * but using shoelace formula for area calculations (works with normalized coordinates)
 * Much more efficient than pixel-by-pixel comparison
 * @param poly1 First polygon points (normalized 0-1)
 * @param poly2 Second polygon points (normalized 0-1)
 * @returns Intersection percentage (0-1)
 */
export const getPolygonIntersectionPercentage = (poly1: IPoint[], poly2: IPoint[]): number => {
    const polygon1: ReturnType<typeof turf.polygon> = pointsToGeoJSONPolygon(poly1);
    const polygon2: ReturnType<typeof turf.polygon> = pointsToGeoJSONPolygon(poly2);

    const featureCollection = turf.featureCollection([polygon1, polygon2]);
    const intersection: ReturnType<typeof turf.intersect> | null = turf.intersect(featureCollection);

    if (!intersection || !intersection.geometry) {
        return 0;
    }

    let intersectionArea = 0;
    const {geometry} = intersection;

    if (geometry.type === 'Polygon') {
        const intersectionCoords = geometry.coordinates[0] as number[][];
        const intersectionPoints: IPoint[] = intersectionCoords.map((coord) => ({
            x: coord[0],
            y: coord[1],
        }));
        intersectionArea = getPolygonArea(intersectionPoints);
    } else if (geometry.type === 'MultiPolygon') {
        // Sum areas of all polygons in MultiPolygon
        const multiCoords = geometry.coordinates as number[][][][];
        multiCoords.forEach((polygonCoords) => {
            const intersectionPoints: IPoint[] = polygonCoords[0].map((coord) => ({
                x: coord[0],
                y: coord[1],
            }));
            const area = getPolygonArea(intersectionPoints);
            intersectionArea += area;
        });
    }

    // Calculate areas using shoelace formula (works with normalized coordinates)
    const area1 = getPolygonArea(poly1);
    const area2 = getPolygonArea(poly2);

    const unionArea = area1 + area2 - intersectionArea;
    if (unionArea === 0) {
        return 0;
    }

    return intersectionArea / unionArea;
};

/**
 * Check if a point is inside a polygon using Turf.js
 * @param point Point to check (normalized 0-1)
 * @param polygon Polygon points (normalized 0-1)
 * @returns True if point is inside polygon
 */
export const isPointInPolygon = (point: IPoint, polygon: IPoint[]): boolean => {
    const polygonGeoJSON = pointsToGeoJSONPolygon(polygon);
    const pointGeoJSON = turf.point([point.x, point.y]);

    return turf.booleanPointInPolygon(pointGeoJSON, polygonGeoJSON);
};
